import type {
  CostGroupSummary,
  CostImportAnalysis,
  CostLaneSummary,
  CostRecommendationHighlight,
  OpportunityLaneId,
} from "./types";

interface ParsedRecommendation extends CostRecommendationHighlight {
  resourceId: string;
  lastRefreshTimestamp: string;
}

const normalized = (value: string) => value.trim().toLowerCase().replace(/[^a-z0-9]/g, "");

const aliases = {
  accountId: ["accountid", "account", "awsaccountid", "awsaccount"],
  region: ["region", "awsregion"],
  actionType: ["actiontype", "action", "recommendedaction"],
  resourceType: ["currentresourcetype", "resourcetype", "resource"],
  monthlyCost: ["estimatedmonthlycost", "monthlycost", "monthlycostusd", "estimatedmonthlycostusd"],
  monthlySavings: ["estimatedmonthlysavings", "monthlysavings", "monthlysavingsusd", "estimatedmonthlysavingsusd"],
  savingsPercentage: ["estimatedsavingspercentage", "savingspercentage", "savingspercent", "savings"],
  implementationEffort: ["implementationeffort", "effort"],
  restartNeeded: ["restartneeded", "isresourcerestartneeded", "requiresrestart"],
  rollbackPossible: ["rollbackpossible", "isrollbackpossible", "reversible"],
  currentSummary: ["currentresourcesummary", "currentsummary", "currentconfiguration", "summary"],
  recommendedSummary: ["recommendedresourcesummary", "recommendedsummary", "recommendedconfiguration"],
  resourceId: ["resourceid", "resourcearn", "arn"],
  lastRefreshTimestamp: ["lastrefreshtimestamp", "lastrefresh", "refreshedat"],
} as const;

function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    if (quoted) {
      if (character === '"' && text[index + 1] === '"') {
        cell += '"';
        index += 1;
      } else if (character === '"') quoted = false;
      else cell += character;
    } else if (character === '"') quoted = true;
    else if (character === ",") {
      row.push(cell);
      cell = "";
    } else if (character === "\n") {
      row.push(cell.replace(/\r$/, ""));
      if (row.some((value) => value.trim())) rows.push(row);
      row = [];
      cell = "";
    } else cell += character;
  }
  row.push(cell.replace(/\r$/, ""));
  if (row.some((value) => value.trim())) rows.push(row);
  return rows;
}

const numberValue = (value: string) => {
  const parsed = Number(value.replace(/[$,%\s]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const booleanValue = (value: string): boolean | null => {
  if (!value.trim()) return null;
  if (["true", "yes", "1", "y"].includes(value.trim().toLowerCase())) return true;
  if (["false", "no", "0", "n"].includes(value.trim().toLowerCase())) return false;
  return null;
};

const laneForAction = (action: string): OpportunityLaneId => {
  const key = normalized(action);
  if (key.includes("purchase")) return "commitments";
  if (key.includes("graviton")) return "graviton";
  if (key.includes("delete") || key.includes("stop") || key.includes("upgrade")) return "hygiene";
  return "rightsizing";
};

const humanize = (value: string) => value
  .replace(/([a-z])([A-Z])/g, "$1 $2")
  .replace(/[_-]+/g, " ")
  .replace(/\b\w/g, (character) => character.toUpperCase());

const rounded = (value: number) => Math.round(value * 100) / 100;
const percentage = (value: number, total: number) => total > 0 ? Math.round((value / total) * 1000) / 10 : 0;

function summarizeGroups(rows: ParsedRecommendation[], keyFor: (row: ParsedRecommendation) => string, total: number): CostGroupSummary[] {
  const groups = new Map<string, { count: number; savings: number }>();
  rows.forEach((row) => {
    const key = keyFor(row) || "Unknown";
    const current = groups.get(key) ?? { count: 0, savings: 0 };
    current.count += 1;
    current.savings += row.monthlySavings;
    groups.set(key, current);
  });
  return [...groups.entries()]
    .map(([key, value]) => ({
      key,
      label: humanize(key),
      recommendationCount: value.count,
      monthlySavings: rounded(value.savings),
      shareOfSavings: percentage(value.savings, total),
    }))
    .sort((a, b) => b.monthlySavings - a.monthlySavings || b.recommendationCount - a.recommendationCount);
}

export function analyzeCostOptimizationCsv(text: string, fileName: string): CostImportAnalysis {
  const parsed = parseCsv(text.replace(/^\uFEFF/, ""));
  if (parsed.length < 2) throw new Error("This CSV does not contain any recommendation rows.");
  const headers = parsed[0].map(normalized);
  const findIndex = (keys: readonly string[]) => headers.findIndex((header) => keys.includes(header));
  const indexes = Object.fromEntries(Object.entries(aliases).map(([key, keys]) => [key, findIndex(keys)])) as Record<keyof typeof aliases, number>;
  if (indexes.actionType < 0 || indexes.monthlySavings < 0) {
    throw new Error("We could not find the AWS action and estimated monthly savings columns. Export the Savings opportunities table from Cost Optimization Hub, then try again.");
  }
  const valueAt = (row: string[], index: number) => index >= 0 ? (row[index] ?? "").trim() : "";
  const rows = parsed.slice(1).map((row): ParsedRecommendation => ({
    accountId: valueAt(row, indexes.accountId) || "Unknown",
    region: valueAt(row, indexes.region) || "Global",
    actionType: valueAt(row, indexes.actionType) || "Unknown",
    resourceType: valueAt(row, indexes.resourceType) || "Unknown",
    monthlyCost: numberValue(valueAt(row, indexes.monthlyCost)),
    monthlySavings: numberValue(valueAt(row, indexes.monthlySavings)),
    savingsPercentage: numberValue(valueAt(row, indexes.savingsPercentage)),
    implementationEffort: valueAt(row, indexes.implementationEffort) || "Unknown",
    restartNeeded: booleanValue(valueAt(row, indexes.restartNeeded)),
    rollbackPossible: booleanValue(valueAt(row, indexes.rollbackPossible)),
    currentSummary: valueAt(row, indexes.currentSummary),
    recommendedSummary: valueAt(row, indexes.recommendedSummary),
    resourceId: valueAt(row, indexes.resourceId),
    lastRefreshTimestamp: valueAt(row, indexes.lastRefreshTimestamp),
  })).filter((row) => row.monthlySavings > 0);
  if (!rows.length) throw new Error("No recommendations with a positive estimated monthly savings value were found.");

  const monthlyCostCoverage = rows.filter((row) => row.monthlyCost > 0).length;
  const hasCompleteMonthlyCost = indexes.monthlyCost >= 0 && monthlyCostCoverage === rows.length;
  const totalMonthlyCost = rows.reduce((sum, row) => sum + row.monthlyCost, 0);
  const totalMonthlySavings = rows.reduce((sum, row) => sum + row.monthlySavings, 0);
  const sorted = [...rows].sort((a, b) => b.monthlySavings - a.monthlySavings);
  const topTenSavings = sorted.slice(0, 10).reduce((sum, row) => sum + row.monthlySavings, 0);
  const laneLabels: Record<OpportunityLaneId, string> = {
    commitments: "Commitment purchases",
    graviton: "Graviton migration",
    rightsizing: "Rightsizing",
    hygiene: "Storage and idle resources",
  };
  const resourceSummaries = summarizeGroups(rows, (row) => row.resourceType, totalMonthlySavings);
  const lanes: CostLaneSummary[] = (["commitments", "graviton", "rightsizing", "hygiene"] as OpportunityLaneId[]).map((id) => {
    const laneRows = rows.filter((row) => laneForAction(row.actionType) === id);
    const topReversible = laneRows
      .filter((row) => row.rollbackPossible === true)
      .sort((a, b) => b.monthlySavings - a.monthlySavings)
      .slice(0, 5);
    const monthlySavings = laneRows.reduce((sum, row) => sum + row.monthlySavings, 0);
    const laneResourceGroups = summarizeGroups(laneRows, (row) => row.resourceType, monthlySavings);
    return {
      id,
      label: laneLabels[id],
      recommendationCount: laneRows.length,
      monthlyCost: rounded(laneRows.reduce((sum, row) => sum + row.monthlyCost, 0)),
      monthlySavings: rounded(monthlySavings),
      annualizedSavings: rounded(monthlySavings * 12),
      shareOfSavings: percentage(monthlySavings, totalMonthlySavings),
      restartRequiredCount: laneRows.filter((row) => row.restartNeeded === true).length,
      reversibleCount: laneRows.filter((row) => row.rollbackPossible === true).length,
      topReversibleCount: topReversible.length,
      topReversibleMonthlySavings: rounded(topReversible.reduce((sum, row) => sum + row.monthlySavings, 0)),
      highEffortCount: laneRows.filter((row) => ["high", "veryhigh"].includes(normalized(row.implementationEffort))).length,
      topResourceTypes: laneResourceGroups.slice(0, 3).map((group) => group.label),
    };
  }).sort((a, b) => b.monthlySavings - a.monthlySavings || b.recommendationCount - a.recommendationCount);
  const datedRefreshes = rows
    .map((row) => new Date(row.lastRefreshTimestamp))
    .filter((date) => !Number.isNaN(date.getTime()))
    .sort((a, b) => b.getTime() - a.getTime());
  const warnings: string[] = [];
  if (indexes.resourceType < 0) warnings.push("Resource type was not available; action-level analysis was used.");
  if (indexes.restartNeeded < 0 || indexes.rollbackPossible < 0) warnings.push("Restart or rollback fields were not available; change-safety analysis is limited.");
  if (!hasCompleteMonthlyCost) warnings.push(`Estimated monthly cost was available for ${monthlyCostCoverage} of ${rows.length} recommendations; the portfolio savings rate is not shown.`);

  return {
    fileName,
    importedAt: new Date().toISOString(),
    rowCount: rows.length,
    totalMonthlyCost: rounded(totalMonthlyCost),
    totalMonthlySavings: rounded(totalMonthlySavings),
    annualizedSavings: rounded(totalMonthlySavings * 12),
    savingsRate: hasCompleteMonthlyCost ? percentage(totalMonthlySavings, totalMonthlyCost) : null,
    accountCount: new Set(rows.map((row) => row.accountId).filter((value) => value !== "Unknown")).size,
    regionCount: new Set(rows.map((row) => row.region).filter((value) => value !== "Global")).size,
    latestRefresh: datedRefreshes[0]?.toISOString() ?? null,
    lowEffortSavings: rounded(rows.filter((row) => ["verylow", "low"].includes(normalized(row.implementationEffort))).reduce((sum, row) => sum + row.monthlySavings, 0)),
    noRestartSavings: rounded(rows.filter((row) => row.restartNeeded === false).reduce((sum, row) => sum + row.monthlySavings, 0)),
    reversibleSavings: rounded(rows.filter((row) => row.rollbackPossible === true).reduce((sum, row) => sum + row.monthlySavings, 0)),
    lowValueTailCount: rows.filter((row) => row.monthlySavings < 100).length,
    lowValueTailSavings: rounded(rows.filter((row) => row.monthlySavings < 100).reduce((sum, row) => sum + row.monthlySavings, 0)),
    topTenSavingsShare: percentage(topTenSavings, totalMonthlySavings),
    lanes,
    actionSummaries: summarizeGroups(rows, (row) => row.actionType, totalMonthlySavings),
    resourceSummaries,
    topRecommendations: sorted.slice(0, 8),
    warnings,
  };
}
